"use client";

import React, { useEffect, useState, useRef } from "react";

import { REBOTT_API_LOCATION_BASE } from "@/config";
import { Typography, Button } from "@material-tailwind/react";
import MagnifyingGlassIcon from "@heroicons/react/24/outline/MagnifyingGlassIcon";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import ChevronLeftIcon from "@heroicons/react/24/outline/ChevronLeftIcon";
import ChevronRightIcon from "@heroicons/react/24/outline/ChevronRightIcon";

import { MapPinIcon, MapIcon } from "@heroicons/react/24/solid";
import Link from "next/link";

// โครงสร้างกลางที่เราจะใช้แสดงผล
interface RebottItem {
  id: number;
  location: string;
  bannerImages: { imageUrl: string }[];
  campaignTitle: string;
  publishDate: string;
  expireDate: string;
  latitude: string;
  longitude: string;
}

// 1) API ตัวแรก listSearchPage
interface LocationApiCampaign {
  name?: string;
  imageKey?: string;
  startDate?: string;
  endDate?: string;
}

interface LocationApiRecord {
  name?: string;
  imageKey?: string;
  campaigns?: LocationApiCampaign[];
  location?: {
    name?: string;
    latitude?: string | number;
    longitude?: string | number;
  } | null;
}

interface LocationApiResponse {
  total: number;
  data: LocationApiRecord[];
}

const LOCATION_API_ENDPOINT = "/api/rebott-locations";
const LOCATION_API_ORIGIN = new URL(REBOTT_API_LOCATION_BASE).origin;

function toResourceUrl(imageKey?: string): string {
  if (!imageKey) return "";
  if (/^https?:\/\//i.test(imageKey)) return imageKey;
  const normalizedKey = imageKey.replace(/^\/+/, "");
  return `${LOCATION_API_ORIGIN}/resource/${normalizedKey}`;
}

function normalizeLocationResponse(data: LocationApiResponse): RebottItem[] {
  const records = Array.isArray(data?.data) ? data.data : [];

  return records.flatMap((record, recordIndex) => {
    const locationName = record.location?.name || record.name || "-";
    const latitude = record.location?.latitude != null ? String(record.location.latitude) : "";
    const longitude = record.location?.longitude != null ? String(record.location.longitude) : "";
    const campaigns = record.campaigns?.length ? record.campaigns : [{}];

    return campaigns.map((campaign, campaignIndex) => {
      const bannerImages = [campaign.imageKey, record.imageKey]
        .filter((key): key is string => Boolean(key))
        .map((key) => ({ imageUrl: toResourceUrl(key) }));

      return {
        id: recordIndex * 1000 + campaignIndex + 1,
        location: locationName,
        bannerImages,
        campaignTitle: campaign.name || "-",
        publishDate: campaign.startDate || "",
        expireDate: campaign.endDate || "",
        latitude,
        longitude,
      };
    });
  });
}

export default function RebottLocationsContent() { 
  const [allItems, setAllItems] = useState<RebottItem[]>([]);
  const [display, setDisplay]   = useState<RebottItem[]>([]);
  const [keyword, setKeyword]     = useState("");
  const [openSuggest, setOpenSuggest] = useState(false);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [lightboxUrls, setLightboxUrls] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [ph, setPh] = useState("ค้นหาตู้ Rebott ด้วย Location, Campaign");
  const size = 3;
  const apiPageSize = 1000;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadLocations() {
      const normalizedKeyword = keyword.trim();
      const url = new URL(LOCATION_API_ENDPOINT, window.location.origin);
      url.searchParams.set("pageNumber", "1");
      url.searchParams.set("pageSize", String(apiPageSize));
      if (normalizedKeyword) {
        url.searchParams.set("filter", normalizedKeyword);
      }

      const response = await fetch(url.toString());
      if (!response.ok) {
        setAllItems([]);
        setDisplay([]);
        setTotalElements(0);
        setTotalPages(1);
        return;
      }

      const data = (await response.json()) as LocationApiResponse;
      const normalized = normalizeLocationResponse(data);

      const filtered = normalizedKeyword
        ? normalized.filter((item) =>
            item.location.toLowerCase().includes(normalizedKeyword.toLowerCase()) ||
            item.campaignTitle.toLowerCase().includes(normalizedKeyword.toLowerCase())
          )
        : normalized;

      setAllItems(filtered);
      setTotalElements(filtered.length);
      setTotalPages(Math.max(1, Math.ceil(filtered.length / size)));

      const start = (page - 1) * size;
      setDisplay(filtered.slice(start, start + size));
    }

    loadLocations().catch(() => {
      setAllItems([]);
      setDisplay([]);
      setTotalElements(0);
      setTotalPages(1);
    });
  }, [page, keyword]);
  
  

  // ปิด dropdown เมื่อคลิกนอก
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpenSuggest(false);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // สร้าง dropdown list: ถ้า keyword ว่าง ก็โชว์ allItems ทั้งหมด
  const locationSuggest = keyword.trim()
    ? allItems.filter(it =>
        it.location.toLowerCase().includes(keyword.toLowerCase())
      )
    : allItems;

  const titleSuggest = keyword.trim()
    ? allItems.filter(it =>
        it.campaignTitle
          .toLowerCase()
          .includes(keyword.toLowerCase())
      )
    : allItems;

  // 1. เฉพาะ Location ไม่ซ้ำ
  const uniqueLocationSuggest = Array.from(
    new Set(locationSuggest.map((it) => it.location))
  ).map((loc) =>
    // ดึงออบเจ็กต์ตัวแรกที่มีชื่อ Location ตรงกับ loc
    locationSuggest.find((it) => it.location === loc)!
  );

  // 2. เฉพาะ Campaign ไม่ซ้ำ
  const uniqueTitleSuggest = Array.from(
    new Set(titleSuggest.map((it) => it.campaignTitle))
  ).map((title) =>
    titleSuggest.find((it) => it.campaignTitle === title)!
  );

  const onSearch = () => {
    setOpenSuggest(false);
    setPage(1);
  };

  return (
    // <section className="px-6 py-12 sm:px-5 md:px-[20px] lg:px-[20px] lg:py-12 bg-white">
    <section className="bg-white py-10"> 
     <div className="container mx-auto px-10">
        <Typography {...({} as any)}  variant="h3" className="text-center font-bold mb-6 text-gray-800">
          ตู้ <span className="text-[#2998E2]">Rebott</span> ของเรา<br className="block md:hidden" />ตั้งอยู่ที่ไหนบ้าง?
        </Typography>

        <div ref={ref} className="relative max-w-xl mx-auto mb-8 text-[#2998E2]">
          <input
            type="text"
            placeholder={ph}
            value={keyword}
            onChange={e => {
              setKeyword(e.target.value);
              setPage(1);
              setOpenSuggest(true);
            }}
            onFocus={() => {
              setOpenSuggest(true);
              setPh("");
            }}
            onBlur={() => {
              if (!keyword) setPh("ค้นหาตู้ Rebott ด้วย Location, Campaign");
            }}
            onKeyDown={e => e.key === "Enter" && onSearch()}
            className="w-full border-2 border-blue-600 rounded-lg px-4 py-2 pr-12 text-blue-300 placeholder:text-gray-400 focus:outline-none"
          />
          {keyword && (
            <XMarkIcon
              onClick={() => {
                setKeyword("");
                setPage(1);
                setPh("ค้นหาตู้ Rebott ด้วย Location, Campaign");
              }}
              className="absolute right-11 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 cursor-pointer"
            />
          )}
          <MagnifyingGlassIcon
            onClick={onSearch}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-[#2998E2] cursor-pointer"
          />

          {openSuggest && (
            <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 grid grid-cols-2">
              <div className="p-2 border-r">
                <div className="font-medium mb-1 pl-2 text-gray-700 bg-[#eff6ff]">ค้นหาจาก Location</div>
                {locationSuggest.length > 0 ? (
                  uniqueLocationSuggest.map(it => (
                    <div
                      key={it.id}
                      className="px-2 py-1 pl-2 hover:bg-[#eff6ff] cursor-pointer"
                      onMouseDown={() => {
                           const v = it.location;
                           setKeyword(v);
                           setOpenSuggest(false);
                           setPage(1);
                      }}
                    >
                      {it.location}
                    </div>
                  ))
                ) : (
                  <div className="px-2 py-1 text-gray-500">ไม่พบ</div>
                )}
              </div>

              <div className="p-2">
                <div className="font-medium mb-1 pl-2 text-gray-700 bg-[#eff6ff]">ค้นหาจาก Campaign</div>
                {titleSuggest.length > 0 ? (
                  uniqueTitleSuggest.map(it => (
                    <div
                      key={it.id}
                      className="px-2 py-1 hover:bg-[#eff6ff] cursor-pointer"
                      onMouseDown={() => {
                           const v = it.campaignTitle;
                           setKeyword(v);
                           setOpenSuggest(false);
                           setPage(1);
                      }}
                    >
                      {it.campaignTitle}
                    </div>
                  ))
                ) : (
                  <div className="px-2 py-1 text-gray-500">ไม่พบ</div>
                )}
              </div>
            </div>
          )}
        </div>

        <Typography {...({} as any)}  variant="paragraph" className=" mb-2 text-gray-800 text-sm">
          <span className="font-semibold tracking-wide">ผลการค้นหา: </span>{" "}
          <span className="text-[#2998E2] font-normal ">{keyword || "ตู้ Rebott ทั้งหมด"} (จำนวน {totalElements} ตู้)</span>
        </Typography>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
          {display.map(loc => {
              const pub = new Date(loc.publishDate);
              const exp = new Date(loc.expireDate);
              const now = new Date();

              // ฟังก์ชันช่วยฟอร์แมตเป็น dd/mm/yyyy
              const formatDate = (d: Date) => {
                const dd = String(d.getDate()).padStart(2, "0");
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                const yyyy = d.getFullYear();
                return `${dd}/${mm}/${yyyy}`;
              };

              // เราจะเอา publishDate แบบเต็ม
              const pubText = formatDate(pub);

              // ถ้ายังไม่หมดอายุ ให้แสดงถึงปัจจุบัน ถ้าหมดแล้ว ให้ฟอร์แมต expireDate
              const endText =
                exp >= now ? "ถึงปัจจุบัน" : formatDate(exp);

              const active = new Date(loc.expireDate) >= new Date();
              const imgUrl = loc.bannerImages.length > 0
              ? loc.bannerImages[0].imageUrl
              : "../../image/default-rvm.webp";

              const lat = loc.latitude || "";
              const lon = loc.longitude || "";

            return (
              <div
                key={loc.id}
                className="bg-[#F5FCFE] ring ring-blue-200 ring-2 rounded-xl overflow-hidden"
              >
                <Typography {...({} as any)}  
                  className="flex items-center justify-center gap-1 mt-3 mb-1 font-semibold text-base text-center text-[#2998E2] px-2 py-1"
                >
                  <MapPinIcon className="w-5 h-5 text-[#2998E2]" />
                  {loc.location}
                </Typography>

                <div className="px-4 pt-1"> 
                  <div className="relative w-full aspect-square overflow-hidden rounded-xl cursor-pointer"
                       onClick={() => {
                         const urls = loc.bannerImages.map(b => b.imageUrl);
                         // ถ้าไม่มีรูป ก็ใส่ default
                         setLightboxUrls(urls.length ? urls : ["../../image/default-rvm.webp"]);
                         setLightboxIndex(0);
                    }}>
                    <img
                      src={imgUrl}
                      alt={`${loc.location} - ${loc.campaignTitle}`}
                      className="w-full h-full object-cover"
                    />

                    {loc.bannerImages.length > 1 && (
                      <div 
                      className="
                      absolute bottom-2 right-2 
                      text-white text-xs font-semibold 
                      px-2 py-0.5 rounded  
                      bg-black bg-opacity-30
                      ">
                        +{loc.bannerImages.length - 1}
                      </div>
                    )}
                  </div>
                </div>
                <div className=" flex items-center justify-center pointer-events-none mt-3">
                  <span className="inline-block bg-blue-200 text-xs font-medium px-5 py-0.5 rounded-lg item-center tracking-wide">
                    แคมเปญ
                  </span>
                </div>

                <Typography {...({} as any)}  variant="lead" className="text-base text-center text-[#2998E2] pt-2">
                  {loc.campaignTitle}
                </Typography>

                <div className="px-4 mt-1 text-sm text-gray-600 text-center">
                  <span className="font-semibold tracking-wide">ระยะเวลา: </span> {pubText} – {endText}
                </div>

                <div className="flex justify-between items-center px-4 pb-4 pt-4">
                <Button {...({} as any)}
                    size="sm"
                    className={`
                      inline-flex items-center
                      font-medium
                      pr-4 pl-2 py-1.5
                      transition tracking-wide
                      ${active
                        ? "bg-green-400 hover:bg-green-600 text-white"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-600"
                      }
                    `}
                  >

                    <span
                      className={`
                        w-3 h-3 rounded-full mr-2
                        ${active ? "bg-green-700" : "bg-gray-400"}
                      `}
                    />
                    {active ? "ใช้งานอยู่" : "สิ้นสุดการใช้งาน"}
                  </Button>

                  <Link href={`https://maps.google.com/?q=${lat},${lon}`} target="_blank">
                    <Button {...({} as any)}
                      className="
                        inline-flex items-center
                        bg-blue-400 hover:bg-blue-600
                        text-white font-medium
                        pr-5 pl-4 py-1.5 
                        transition
                        tracking-wide
                        "
                    >
                      <MapIcon className="w-4 h-4 mr-1" />  ดูแผนที่
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-4">
              <button
                aria-label="Swipe left"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className={`
                  px-2 py-[0.35rem] rounded border
                  ${page <= 1
                    ? "border-gray-300 text-gray-300 cursor-not-allowed"
                    : "border-gray-400 text-gray-700 hover:border-gray-600"}
                `}
              >
              <ChevronLeftIcon className="w-5 h-5" />
              </button>

                {Array.from({ length: totalPages }, (_, i) => {
                  const p = i + 1;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`
                        w-8 h-8 flex items-center justify-center
                        rounded border
                        ${page === p
                          ? "bg-gray-900 text-white border-gray-900"
                          : "border-gray-400 text-gray-700 hover:border-gray-600"}
                      `}
                    >
                      {p}
                    </button>
                  );
                })}

                <button
                  aria-label="Swipe right"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className={`
                    px-2 py-[0.35rem] rounded border
                    ${page >= totalPages
                      ? "border-gray-300 text-gray-300 cursor-not-allowed"
                      : "border-gray-400 text-gray-700 hover:border-gray-600"}
                  `}
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
            </div>
          )}
        </div>

            {lightboxUrls.length > 0 && (
              <div
                className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
                onClick={() => setLightboxUrls([])}
              >
                <div className="relative w-full max-w-xl p-4">
                  <button
                    className="absolute top-6 right-11 text-white"
                    onClick={() => setLightboxUrls([])}
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>

                  {lightboxIndex > 0 && (
                    <button
                      className="absolute left-5 top-1/2 transform -translate-y-1/2 text-white"
                      onClick={e => {
                        e.stopPropagation();
                        setLightboxIndex(i => i - 1);
                      }}
                    >
                      <ChevronLeftIcon className="w-8 h-8" />
                    </button>
                  )}

                  <img
                    src={lightboxUrls[lightboxIndex]}
                    alt="Rebott location"
                    className="
                    block
                    mx-auto
                    w-auto        /* ให้ปรับขนาดตามเนื้อหา ไม่บังคับเต็ม */
                    max-w-full    /* แต่ไม่เกินความกว้าง container */
                    max-h-[80vh]  /* สูงสุดไม่เกิน 80% ของ viewport height */
                    rounded-lg
                  "
                  />

                  {lightboxIndex < lightboxUrls.length - 1 && (
                    <button
                      className="absolute right-10 top-1/2 transform -translate-y-1/2 text-white"
                      onClick={e => {
                        e.stopPropagation();
                        setLightboxIndex(i => i + 1);
                      }}
                    >
                      <ChevronRightIcon className="w-8 h-8" />
                    </button>
                  )}
                </div>
              </div>
            )}
    </section>
  );
}


